"use client";

import { useNavigate } from "react-router-dom";
import {
  IconFileImport,
  IconFileExport,
  IconTruck,
  IconShip,
  IconLock,
  IconBuildingWarehouse,
  IconPackageExport,
  IconArrowUpRight,
  IconArrowDownLeft,
  IconArrowsExchange,
  IconPackage,
  IconLogin2,
  IconLogout2,
  IconCertificate,
} from "@tabler/icons-react";
import { useTheme } from "@contexts/ThemeContext";
import { useLanguage } from "@contexts/LanguageContext";
import { DARK_THEME_MODES } from "@constants/colors";
import { motion } from "framer-motion";
import { ChoiceCta, ChoiceTile, choiceColor } from "@components/choice-card";

// Email-based edit access rules (only users with 'edit' can create new documents)
const BC20_EDIT_EMAILS = [
  "christina.vonny@molgroup.com",
  "ari.wibowo@molgroup.com",
  "femy.muthia@molgroup.com",
  "lanny.apriliany@molgroup.com",
  "mlg-id.seaimport01@molgroup.com",
  "mlg-id.airport01@molgroup.com",
];

const BC30_EDIT_EMAILS = [
  "christina.vonny@molgroup.com",
  "ari.wibowo@molgroup.com",
  "femy.muthia@molgroup.com",
  "reza.haqqa@molgroup.com",
  "ratri.puji@molgroup.com",
  "prama.adi@molgroup.com",
  "mlg-id.airexport01@molgroup.com",
];

const getUserEmail = (): string => {
  try {
    const raw = localStorage.getItem("authData");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return (parsed?.email || "").toLowerCase();
  } catch {
    return "";
  }
};

interface Product {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  available: boolean;
  link?: string;
  badge?: string;
  /** Card colour when available: Import blue and Export orange (as manifest Inward/Outward), FTZ teal. */
  tone?: "inward" | "outward" | "ftz";
  /** Document family labels shown on the card (e.g. BC 2.0 / BC 3.0) */
  docCodes?: string[];
}

export default function BuatAjuPage() {
  const navigate = useNavigate();
  const { mode, colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const isDark = DARK_THEME_MODES.includes(mode);

  const userEmail = getUserEmail();
  const canEditImport = !userEmail.endsWith("@molgroup.com") || BC20_EDIT_EMAILS.includes(userEmail);
  const canEditExport = !userEmail.endsWith("@molgroup.com") || BC30_EDIT_EMAILS.includes(userEmail);

  const primaryProducts: Product[] = [
    {
      id: "import",
      title: t("buataju.import"),
      description: t("buataju.importDesc"),
      icon: <IconFileImport size={24} stroke={1.75} />,
      available: canEditImport,
      link: "/doc-pabean/upload?type=import",
      badge: canEditImport ? t("buataju.popular") : t("buataju.limitedAccess"),
      tone: "inward",
      docCodes: ["BC 2.0"],
    },
    {
      id: "export",
      title: t("buataju.export"),
      description: t("buataju.exportDesc"),
      icon: <IconFileExport size={24} stroke={1.75} />,
      available: canEditExport,
      link: "/doc-pabean/upload?type=export",
      badge: canEditExport ? t("buataju.popular") : t("buataju.limitedAccess"),
      tone: "outward",
      docCodes: ["BC 3.0"],
    },
    {
      id: "bc33",
      title: "BC 3.3",
      description: t("buataju.bc33Desc"),
      icon: <IconPackageExport size={24} stroke={1.75} />,
      available: false,
      badge: t("buataju.comingSoon"),
      docCodes: ["BC 3.3"],
    },
    {
      id: "ppftz",
      title: "PPFTZ",
      description: t("buataju.ppftzDesc"),
      icon: <IconBuildingWarehouse size={24} stroke={1.75} />,
      available: true,
      link: "/doc-pabean/upload?type=ppftz",
      badge: t("buataju.new"),
      tone: "ftz",
      docCodes: ["FTZ 511"],
    },
  ];

  // TPB products from Beacukai OpenAPI (3.TPB) — unique Schema/Payload pairs
  const tpbProducts: Product[] = [
    {
      id: "bc16",
      title: "BC 1.6",
      description: t("buataju.bc16Desc"),
      icon: <IconShip size={24} stroke={1.75} />,
      available: false,
      badge: t("buataju.comingSoon"),
      docCodes: ["BC 1.6"],
    },
    {
      id: "bc23",
      title: "BC 2.3",
      description: t("buataju.bc23Desc"),
      icon: <IconTruck size={24} stroke={1.75} />,
      available: false,
      badge: t("buataju.comingSoon"),
      docCodes: ["BC 2.3"],
    },
    {
      id: "bc25",
      title: "BC 2.5",
      description: t("buataju.bc25Desc"),
      icon: <IconPackageExport size={24} stroke={1.75} />,
      available: false,
      badge: t("buataju.comingSoon"),
      docCodes: ["BC 2.5"],
    },
    {
      id: "bc261",
      title: "BC 2.6.1",
      description: t("buataju.bc261Desc"),
      icon: <IconArrowUpRight size={24} stroke={1.75} />,
      available: false,
      badge: t("buataju.comingSoon"),
      docCodes: ["BC 2.6.1"],
    },
    {
      id: "bc262",
      title: "BC 2.6.2",
      description: t("buataju.bc262Desc"),
      icon: <IconArrowDownLeft size={24} stroke={1.75} />,
      available: false,
      badge: t("buataju.comingSoon"),
      docCodes: ["BC 2.6.2"],
    },
    {
      id: "bc27",
      title: "BC 2.7",
      description: t("buataju.bc27Desc"),
      icon: <IconArrowsExchange size={24} stroke={1.75} />,
      available: false,
      badge: t("buataju.comingSoon"),
      docCodes: ["BC 2.7"],
    },
    {
      id: "bc28",
      title: "BC 2.8",
      description: t("buataju.bc28Desc"),
      icon: <IconPackage size={24} stroke={1.75} />,
      available: false,
      badge: t("buataju.comingSoon"),
      docCodes: ["BC 2.8"],
    },
    {
      id: "bc40",
      title: "BC 4.0",
      description: t("buataju.bc40Desc"),
      icon: <IconLogin2 size={24} stroke={1.75} />,
      available: false,
      badge: t("buataju.comingSoon"),
      docCodes: ["BC 4.0"],
    },
    {
      id: "bc41",
      title: "BC 4.1",
      description: t("buataju.bc41Desc"),
      icon: <IconLogout2 size={24} stroke={1.75} />,
      available: false,
      badge: t("buataju.comingSoon"),
      docCodes: ["BC 4.1"],
    },
    {
      id: "bcp3bet",
      title: "BC P3BET",
      description: t("buataju.bcp3betDesc"),
      icon: <IconCertificate size={24} stroke={1.75} />,
      available: false,
      badge: t("buataju.comingSoon"),
      docCodes: ["BC P3BET"],
    },
  ];

  const products = [...primaryProducts, ...tpbProducts];

  const handleSelect = (p: Product) => {
    if (!p.available || !p.link) return;
    navigate(p.link);
  };

  const mutedIconBg = isDark ? themeColors.formFields : "#F1F3F5";
  const mutedIconColor = themeColors.placeholder ?? themeColors.textSecondary;
  const availableCount = products.length;

  const renderCard = (p: Product) => {
    const disabled = !p.available || !p.link;
    const color = !disabled && p.tone ? choiceColor(p.tone, isDark) : themeColors.textSecondary;

    return (
      <div
        key={p.id}
        data-tour={`buat-aju-${p.id}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => handleSelect(p)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleSelect(p);
          }
        }}
        className={[
          "group flex min-h-[250px] flex-col gap-3.5 rounded-xl border p-5 transition-[border-color,transform] duration-150",
          "border-[color:var(--card-border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--c)]",
          disabled
            ? "cursor-not-allowed"
            : "cursor-pointer hover:-translate-y-0.5 hover:border-[color:var(--c)] motion-reduce:hover:translate-y-0",
        ].join(" ")}
        style={{ backgroundColor: themeColors.card, "--c": color, "--card-border": themeColors.border } as React.CSSProperties}
      >
        {/* Top: icon + badge */}
        <div className="flex items-start justify-between gap-2.5">
          {disabled ? (
            <span
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
              style={{ backgroundColor: mutedIconBg, color: mutedIconColor }}
            >
              {p.icon}
            </span>
          ) : (
            <ChoiceTile color={color} size={48}>
              {p.icon}
            </ChoiceTile>
          )}

          {p.badge && (
            <span
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2.5 py-[3px] text-xs font-semibold"
              style={
                disabled
                  ? { borderColor: themeColors.border, color: themeColors.textSecondary }
                  : { borderColor: `${color}8C`, color }
              }
            >
              {disabled && <IconLock size={12} stroke={2.2} />}
              {p.badge}
            </span>
          )}
        </div>

        {/* Content */}
        <h3 className="m-0 text-[22px] font-extrabold leading-tight tracking-tight" style={{ color }}>
          {p.title}
        </h3>
        <p className="m-0 flex-1 text-[13px] leading-[1.55]" style={{ color: themeColors.textSecondary }}>
          {p.description}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t pt-3.5" style={{ borderColor: themeColors.border }}>
          <div className="flex min-w-0 flex-wrap gap-1.5">
            {p.docCodes?.map((docCode) => (
              <span
                key={docCode}
                className="whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-semibold"
                style={{ borderColor: themeColors.border, color: disabled ? themeColors.textSecondary : themeColors.text }}
              >
                {docCode}
              </span>
            ))}
          </div>
          {disabled ? (
            <span
              className="inline-flex shrink-0 items-center gap-1.5 text-[13px] font-semibold"
              style={{ color: themeColors.textSecondary }}
            >
              <IconLock size={14} stroke={2} />
              {t("buataju.locked")}
            </span>
          ) : (
            <ChoiceCta color={color}>{t("buataju.start")}</ChoiceCta>
          )}
        </div>
      </div>
    );
  };

  return (
    // One slide-up on open, no per-card stagger.
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-[calc(100vh-4rem)] px-6 py-10 md:px-12 md:py-12"
      style={{ backgroundColor: themeColors.background }}
    >
      <div className="mx-auto max-w-[1240px]">
        {/* Header */}
        <div
          className="mb-8 flex flex-col gap-6 md:mb-8 md:flex-row md:items-end md:justify-between"
        >
          <div className="max-w-[620px]">
            <h1
              className="mb-2.5 text-[28px] font-extrabold leading-[1.1] tracking-tight md:text-[34px]"
              style={{ color: themeColors.text }}
            >
              {t("buataju.title")}
            </h1>
            <p className="m-0 text-[15px] font-normal leading-[1.6]" style={{ color: themeColors.textSecondary }}>
              {t("buataju.subtitle")}
            </p>
          </div>

          <div
            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-[10px] border px-3.5 py-2 text-[13px] font-semibold"
            style={{
              backgroundColor: themeColors.card,
              borderColor: themeColors.border,
              color: themeColors.text,
            }}
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{
                backgroundColor: themeColors.success ?? "#16A34A",
                boxShadow: `0 0 0 3px ${(themeColors.success ?? "#16A34A")}26`,
              }}
            />
            {availableCount} {t("buataju.typesAvailable")}
          </div>
        </div>

        {/* Primary cards */}
        <div
          data-tour="buat-aju-grid"
          className="grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {primaryProducts.map(renderCard)}
        </div>

        {/* TPB section */}
        <div className="mt-10">
          <div className="mb-4 flex items-center gap-3">
            <h2
              className="m-0 text-[15px] font-bold tracking-tight"
              style={{ color: themeColors.text }}
            >
              {t("buataju.tpb")}
            </h2>
            <span
              className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
              style={{
                backgroundColor: isDark ? themeColors.border : "#F1F3F5",
                color: themeColors.textSecondary,
              }}
            >
              {t("buataju.tpbDesc")}
            </span>
            <div
              className="h-px flex-1"
              style={{ backgroundColor: themeColors.border }}
            />
          </div>

          <div
            className="grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {tpbProducts.map(renderCard)}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
